import React, { RefObject, useEffect, useRef, useState } from 'react';
import Notification from '@Common/Notification';
import Modal61 from '@Common/Modal/Modal61';
import { MultiSelect } from 'primereact/multiselect';
import { useAssignCopies } from '@Apis/locationTasks';
import type { Modal61Ref } from '@Types/common';
import { Option } from '../../../../types/common';

const MAX_COPIES = 5;

interface AssignCopiesModalProps {
    assignCopiesModalRef: RefObject<Modal61Ref | null>;
    locationTaskId: number;
    taskName: string;
    responsibleUsers: Option[];
    onBeforeSaveAndView: () => Promise<boolean>;
    onAfterSaveAndView?: (taskName: string) => Promise<void> | void;
    onCopiesCreated?: () => void;
}

const AssignCopiesModal = (props: AssignCopiesModalProps) => {
    const [selectedUsers, setSelectedUsers] = useState<Option[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isMountedRef = useRef(true);
    const assignCopiesMutation = useAssignCopies();

    useEffect(() => () => { isMountedRef.current = false; }, []);

    const count = selectedUsers.length;

    const handleClose = () => {
        props.assignCopiesModalRef.current?.hide();
        setSelectedUsers([]);
    };

    const handleSelectionChange = (users: Option[]) => {
        if (users.length > MAX_COPIES) {
            Notification('error', 'Too Many People Selected', `You can assign copies to at most ${MAX_COPIES} people at a time.`);
            setSelectedUsers(users.slice(0, MAX_COPIES));
            return;
        }
        setSelectedUsers(users);
    };

    const submit = async (andView: boolean) => {
        if (isSubmitting || count === 0 || count > MAX_COPIES) return;

        setIsSubmitting(true);
        try {
            // Persist pending drawer edits BEFORE creating copies so the copies reflect the
            // task's current state rather than a stale pre-edit snapshot.
            if (andView) {
                const savedOk = await props.onBeforeSaveAndView();
                if (!savedOk) return;
            }

            const request = {
                locationTaskId: props.locationTaskId,
                responsibleUserIds: selectedUsers.map((user) => user.id),
            };
            const response = await assignCopiesMutation.mutateAsync(request);

            const createdCount = response?.createdCount ?? 0;
            Notification('success', 'Copies Created', `${createdCount} ${createdCount === 1 ? 'copy' : 'copies'} created.`);
            handleClose();
            if (andView && props.onAfterSaveAndView) {
                await props.onAfterSaveAndView(props.taskName);
            } else {
                props.onCopiesCreated?.();
            }
        } catch (error) {
            const message = error instanceof Error && error.message ? error.message : 'Failed to create copies. Please try again.';
            Notification('error', 'Error', message);
        } finally {
            if (isMountedRef.current) {
                setIsSubmitting(false);
            }
        }
    };

    return <Modal61
        ref={props.assignCopiesModalRef}
        title='Duplicate'
        size='md'
        isLoading={isSubmitting}
        body={(
            <>
                <div className='mb-1'>
                    <label htmlFor='assignCopiesResponsibleUsers'>Who should a copy of this task be assigned to?</label>
                </div>
                <MultiSelect
                    inputId='assignCopiesResponsibleUsers'
                    optionLabel='name'
                    className='w-100 assign-copies-multiselect'
                    display='chip'
                    filter
                    showClear
                    value={selectedUsers}
                    options={props.responsibleUsers}
                    onChange={(e) => handleSelectionChange(e.value)}
                />
                <p style={{ marginTop: '12px', fontSize: '13px', color: '#6c757d' }}>
                    {count} {count === 1 ? 'copy' : 'copies'} will be created (max {MAX_COPIES})
                </p>
            </>
        )}
        customFooter={(
            <>
                <button
                    type='button'
                    className='btn btn-primary'
                    onClick={() => submit(false)}
                    disabled={isSubmitting || count === 0 || count > MAX_COPIES}
                >
                    Save
                </button>
                {props.onAfterSaveAndView && (
                    <button
                        type='button'
                        className='btn btn-primary'
                        onClick={() => submit(true)}
                        disabled={isSubmitting || count === 0 || count > MAX_COPIES}
                    >
                        Save and View
                    </button>
                )}
            </>
        )}
        onCloseClick={handleClose}
    />
};

export default AssignCopiesModal;
